import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { buildPublicUrl } from '../../../utils/url'
import { useAuthStore } from '../../../store/authStore'
import { useVCardStore } from '../../../store/vCardStore'
import { 
  DocumentTextIcon, 
  QrCodeIcon, 
  ShareIcon, 
  PencilIcon,
  EyeIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import Button from '../../../components/common/Button'

function Dashboard() {
  const { user } = useAuthStore()
  const { vCards, loading, fetchUserVCards } = useVCardStore()

  useEffect(() => {
    if (user?.uid) {
      fetchUserVCards(user.uid)
    }
  }, [user?.uid, fetchUserVCards])

  const userVCard = vCards[0]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          ¡Bienvenido, {user?.displayName}!
        </h1>
        <p className="text-gray-600">
          {userVCard 
            ? 'Aquí puedes gestionar tu Dkard y ver estadísticas.'
            : 'Comienza creando tu primera Dkard profesional.'
          }
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                {userVCard ? 'Editar Dkard' : 'Crear Dkard'}
              </h3>
              <p className="text-sm text-gray-500">
                {userVCard ? 'Actualiza tu información' : 'Diseña tu tarjeta digital'}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Link to={userVCard ? `/editor/${userVCard.id}` : '/editor'}>
              <Button variant="primary" size="small">
                {userVCard ? <PencilIcon className="h-4 w-4 mr-2" /> : <PlusIcon className="h-4 w-4 mr-2" />}
                {userVCard ? 'Editar' : 'Crear'}
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EyeIcon className="h-8 w-8 text-accent-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Vista Previa</h3>
              <p className="text-sm text-gray-500">
                Mira cómo se ve tu Dkard
              </p>
            </div>
          </div>
          <div className="mt-4">
            {userVCard ? (
              <Link to={buildPublicUrl(userVCard.slug).replace(window.location.origin, '')} target="_blank">
                <Button variant="accent" size="small">
                  <EyeIcon className="h-4 w-4 mr-2" />
                  Ver
                </Button>
              </Link>
            ) : (
              <Button variant="secondary" size="small" disabled>
                Crea primero tu Dkard
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShareIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Compartir</h3>
              <p className="text-sm text-gray-500">
                Comparte tu Dkard
              </p>
            </div>
          </div>
          <div className="mt-4">
            {userVCard ? (
              <Button variant="secondary" size="small" onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: `${userVCard.name} - Dkard`,
                    text: `Conoce a ${userVCard.name}`,
                    url: buildPublicUrl(userVCard.slug)
                  })
                } else {
                  navigator.clipboard.writeText(buildPublicUrl(userVCard.slug))
                  toast.success('Enlace copiado al portapapeles')
                }
              }}>
                <ShareIcon className="h-4 w-4 mr-2" />
                Compartir
              </Button>
            ) : (
              <Button variant="secondary" size="small" disabled>
                Crea primero tu Dkard
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Dkard Preview */}
      {userVCard && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Tu Dkard</h2>
            <Link to={`/editor/${userVCard.id}`}>
              <Button variant="primary" size="small">
                <PencilIcon className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            {userVCard.photoUrl && (
              <img
                src={userVCard.photoUrl}
                alt={userVCard.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">{userVCard.name}</h3>
              <p className="text-gray-600">{userVCard.position}</p>
              <p className="text-sm text-gray-500">{userVCard.email}</p>
            </div>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <Link to={buildPublicUrl(userVCard.slug).replace(window.location.origin, '')} target="_blank">
              <Button variant="secondary" size="small">
                <EyeIcon className="h-4 w-4 mr-2" />
                Ver
              </Button>
            </Link>
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(buildPublicUrl(userVCard.slug))
                toast.success('Enlace copiado al portapapeles')
              }}
            >
              Copiar enlace
            </Button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Dkards creadas</p>
              <p className="text-2xl font-semibold text-gray-900">{vCards.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EyeIcon className="h-8 w-8 text-accent-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Vistas totales</p>
              <p className="text-2xl font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShareIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Compartidos</p>
              <p className="text-2xl font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <QrCodeIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Escaneos QR</p>
              <p className="text-2xl font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
